import { currentQueuedModalAtom } from '@/atoms/modalQueueAtoms';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useIsClinicalRecordingActive } from '@/hooks/useIsClinicalRecordingActive';
import { useOnboardingEligibility } from '@/hooks/useOnboardingEligibility';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { resolveUpdateChannel } from '@/tauri/updater/channel';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import {
  detectDictateDownloadDevice,
  DictateDesktopDownloadDialog,
  isDictateDesktopDownloadDialogOpenAtom,
  pendingDictateDesktopDownloadAtom,
  requestedDictateDesktopInstallerAtom,
} from '@heidi/dictate';
import {
  buildDictateInstallerUrl,
  DICTATE_DOWNLOAD_INSTALLER_PARAM,
  DICTATE_DOWNLOAD_INTENT_PARAM,
  DICTATE_DOWNLOAD_INTENT_VALUE,
  DICTATE_INSTALLER_DOWNLOAD_FRAME,
  isDictateDesktopInstaller,
  type DictateDesktopInstaller,
  type DictateDownloadAttempt,
  type DictateDownloadTarget,
  type DictateInstallerChannel,
} from '@heidi/dictate/contracts';
import { useQueryUser } from '@heidi/workspace/data';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isOnOnboardingOwnedPath } from '@/app-shell/app-root/global-modals-cloud-only.conditions';

type InstallHelp = {
  target: DictateDownloadTarget;
  attempt: DictateDownloadAttempt;
  /** The channel the automatic download used; manual links stay on it. */
  channel: DictateInstallerChannel;
};

/**
 * A page the user actually lands on. `/scribe` itself only resolves where to
 * send them, and `/scribe/new-user` (with its steps) is personalization.
 */
const isFinalAppPath = (pathname: string): boolean =>
  pathname.startsWith('/scribe/') &&
  pathname !== '/scribe/new-user' &&
  !pathname.startsWith('/scribe/new-user/') &&
  !isOnOnboardingOwnedPath(pathname);

const targetForInstaller = (installer: DictateDesktopInstaller): DictateDownloadTarget =>
  installer === 'mac-apple-silicon' || installer === 'mac-intel'
    ? { os: 'mac', installer }
    : { os: 'windows', installer };
const cleanDownloadIntentFromPath = (asPath: string): string => {
  const [pathAndSearch = '', hash = ''] = asPath.split('#', 2);
  const [path = '', search = ''] = pathAndSearch.split('?', 2);
  const params = new URLSearchParams(search);
  params.delete(DICTATE_DOWNLOAD_INTENT_PARAM);
  params.delete(DICTATE_DOWNLOAD_INSTALLER_PARAM);
  const nextSearch = params.toString();
  return `${path}${nextSearch ? `?${nextSearch}` : ''}${hash ? `#${hash}` : ''}`;
};

/**
 * The app half of the Dictate download.
 *
 * For a visitor who came through `/dictate/get-started` (the only place that
 * sets the pending intent, so there is no release flag here), once every
 * onboarding step is done and they have landed on a real page with nothing
 * else on screen, it asks the browser to download the installer ONCE: the
 * build the visitor picked on the marketing page, or else the one detected for
 * this computer. It does so by
 * pointing a hidden iframe at the CrabNebula URL — no popup, no Blob buffering
 * of a ~250 MB file, and the app stays put even if the CDN answers with an
 * error page. Then it opens the install-help dialog. The channel comes from the
 * AUTHENTICATED region only: until it is known the intent waits, because a
 * UK/EU user must never get the ROW build.
 *
 * The page cannot see whether the file finished saving, so the dialog only
 * says the download is starting, and offers manual links (which navigate the
 * same iframe) for a blocked or wrong build.
 */
export const DictateDesktopDownloadHost = () => {
  const router = useRouter();
  const { asPath, isReady: isRouterReady, pathname, query } = router;
  const { isAuthenticated, isLoading: isAuthLoading } = useIsAuthenticated();
  const { data: user } = useQueryUser();
  const { isOnboarded, isPending: isEligibilityPending } = useOnboardingEligibility();
  const isClinicalRecordingActive = useIsClinicalRecordingActive(false);
  const kindeProperties = useQueryKindeProperties();
  const currentModal = useAtomValue(currentQueuedModalAtom);
  const isDownloadPending = useAtomValue(pendingDictateDesktopDownloadAtom);
  const setIsDialogOpen = useSetAtom(isDictateDesktopDownloadDialogOpenAtom);
  const store = useStore();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [installHelp, setInstallHelp] = useState<InstallHelp | null>(null);
  const [shouldOpenHelp, setShouldOpenHelp] = useState(false);

  const channel: DictateInstallerChannel | undefined =
    kindeProperties.isSuccess && !kindeProperties.isFetching
      ? resolveUpdateChannel(kindeProperties.data?.region)
      : undefined;
  const staging = getEnvironment() !== ENV_NAME.PROD;

  useEffect(() => {
    if (!isRouterReady) return;
    if (query[DICTATE_DOWNLOAD_INTENT_PARAM] !== DICTATE_DOWNLOAD_INTENT_VALUE) return;

    const installer = query[DICTATE_DOWNLOAD_INSTALLER_PARAM];
    store.set(
      requestedDictateDesktopInstallerAtom,
      isDictateDesktopInstaller(installer) ? installer : null
    );
    store.set(pendingDictateDesktopDownloadAtom, true);
    void router.replace(cleanDownloadIntentFromPath(asPath), undefined, {
      shallow: true,
    });
  }, [asPath, isRouterReady, pathname, query, router, store]);

  const isReadyToDownload =
    isRouterReady &&
    !isTauri() &&
    !isAuthLoading &&
    isAuthenticated &&
    !!user &&
    !isEligibilityPending &&
    isOnboarded &&
    !isClinicalRecordingActive &&
    isFinalAppPath(pathname) &&
    currentModal === null &&
    channel !== undefined;

  useEffect(() => {
    if (!isReadyToDownload || !isDownloadPending || channel === undefined) return;
    // Losing readiness (or a StrictMode replay) cancels this run; the intent
    // stays pending for the next one.
    let isCancelled = false;

    // A build picked on the marketing page wins over detection.
    const requested = store.get(requestedDictateDesktopInstallerAtom);
    const requestedInstaller = isDictateDesktopInstaller(requested) ? requested : null;
    const source = requestedInstaller ? 'picked' : 'detected';
    const resolveTarget = requestedInstaller
      ? Promise.resolve(targetForInstaller(requestedInstaller))
      : detectDictateDownloadDevice();

    void resolveTarget.then((target) => {
      if (isCancelled || !store.get(pendingDictateDesktopDownloadAtom)) return;
      // Claim, then dispatch, with nothing in between: no second download from
      // a remount, a replayed effect or a query update.
      store.set(pendingDictateDesktopDownloadAtom, false);
      store.set(requestedDictateDesktopInstallerAtom, null);

      let attempt: DictateDownloadAttempt = 'not-requested';
      if (target.installer !== null) {
        try {
          const frame = frameRef.current;
          if (!frame) throw new Error('missing_download_frame');
          frame.src = buildDictateInstallerUrl(target.installer, { channel, staging });
          attempt = 'requested';
        } catch {
          logger.warn('Dictate installer download could not start', {
            reason: frameRef.current ? 'dispatch-failed' : 'frame-not-mounted',
            installer: target.installer,
          });
          attempt = 'failed';
        }
      }
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.UiInteraction,
        props: {
          action_type: 'navigate',
          element_type: 'link',
          element_name: 'dictate_desktop_download_auto',
          page_name: ScreenName.DictateDesktopDownload,
          page_section: 'installer_download',
          element_variant: `${source}:${target.installer ?? 'unsupported'}:${attempt}:${channel}`,
        },
      });
      setInstallHelp({ target, attempt, channel });
      setShouldOpenHelp(true);
    });

    return () => {
      isCancelled = true;
    };
  }, [isReadyToDownload, isDownloadPending, channel, staging, store]);

  // Open one commit after the dialog mounts (closed), so it sees the open
  // transition it waits on before loading its body.
  useEffect(() => {
    if (!shouldOpenHelp) return;
    setShouldOpenHelp(false);
    setIsDialogOpen(true);
  }, [shouldOpenHelp, setIsDialogOpen]);

  const onViewed = useCallback(() => {
    track({
      options: { amplitude: false },
      event_name: TrackingEventName.ModalViewed,
      props: { screen_name: ScreenName.DictateDesktopDownload },
    });
  }, []);

  // The clicked link itself downloads (it targets the iframe); this only
  // updates the guide and reports the click.
  const onInstallerClicked = useCallback((installer: DictateDesktopInstaller) => {
    setInstallHelp(
      (current) =>
        current && {
          ...current,
          target: targetForInstaller(installer),
          attempt: 'requested',
        }
    );
    track({
      options: { amplitude: false },
      event_name: TrackingEventName.ButtonClicked,
      props: {
        action: ButtonAction.DictateDesktopDownloadInstallerClicked,
        label: installer,
        screen_name: ScreenName.DictateDesktopDownload,
      },
    });
  }, []);

  return (
    <>
      {/* Kept mounted while the app is: closing the dialog must not cancel a
          download still in flight. */}
      <iframe
        ref={frameRef}
        name={DICTATE_INSTALLER_DOWNLOAD_FRAME}
        title="Heidi installer download"
        aria-hidden="true"
        tabIndex={-1}
        className="hidden"
      />
      {installHelp ? (
        <DictateDesktopDownloadDialog
          target={installHelp.target}
          attempt={installHelp.attempt}
          channel={installHelp.channel}
          staging={staging}
          onViewed={onViewed}
          onInstallerClicked={onInstallerClicked}
        />
      ) : null}
    </>
  );
};
